/**
 * NavigationItem Component
 *
 * Single navigation menu item with:
 * - Active state styling
 * - Hover effects
 * - Icon support
 * - Keyboard accessible
 * - Dark mode support
 *
 * @module NavigationItem
 */

import React from 'react';
import PropTypes from 'prop-types';
import { IconExternalLink } from '@tabler/icons-react';

/**
 * NavigationItem - Single menu item
 *
 * @param {Object} props
 * @param {Object} props.item - Menu item data
 * @param {string} props.item.id - Unique identifier
 * @param {string} props.item.label - Display label
 * @param {React.Component} props.item.icon - Icon component
 * @param {string} [props.item.path] - Route path
 * @param {boolean} [props.isActive] - Is active
 * @param {Function} [props.onClick] - Click handler
 */
const NavigationItem = ({ item, isActive = false, onClick }) => {
  const Icon = item.icon;
  const Element = item.external ? 'a' : 'button';
  const elementProps = item.external
    ? {
        href: item.path,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : { type: 'button' };

  return (
    <Element
      {...elementProps}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5
        cursor-pointer
        text-[13px] font-medium relative
        ${isActive
          ? 'text-text-primary'
          : 'text-text-tertiary hover:text-text-secondary hover:bg-surface-3'
        }
        focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2
        focus:ring-offset-surface-1
      `}
      style={isActive ? {
        background: 'var(--accent-muted)',
        color: 'var(--accent)',
      } : {}}
      onClick={item.external ? undefined : onClick}
      aria-current={isActive ? 'page' : undefined}
      aria-label={item.label}
    >
      {isActive && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4"
          style={{ background: 'var(--accent)' }}
        />
      )}
      {Icon && (
        <Icon
          className="w-[18px] h-[18px] flex-shrink-0"
          aria-hidden="true"
        />
      )}
      <span className="flex-1 text-left truncate">
        {item.label}
      </span>
      {item.external && <IconExternalLink className="h-3.5 w-3.5" aria-hidden="true" />}
    </Element>
  );
};

NavigationItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    path: PropTypes.string,
    external: PropTypes.bool
  }).isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func
};

export default React.memo(NavigationItem);
